/**
 * Compare EPDs side by side: traits down, animals across, with accuracy and percentile rank. One
 * association at a time, and different runs are called out, never mixed silently.
 */
import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { RefPicker } from "../../../web/forms/pickers";
import { formatDate } from "../../../web/lib/format";
import { t, useLanguage } from "../../../web/lib/i18n";
import { useApi } from "../../../web/lib/query";
import { useCan } from "../../../web/lib/session";
import { Card, Chip, Disclosure, Empty, ErrorNote, Field, Icon, Loading, Section, Segmented, cx } from "../../../web/ui/kit";
import { associationKey, associationShort, formatEpd, runsOf, type EvaluationSnapshot } from "../shared/epd";
import { DEFAULT_TRAITS, epdOrder } from "../shared/traits";
import type { EpdAnimal } from "../shared/types";
import { GeneticsShell } from "./GeneticsWorkspace";
import { ImportPanel } from "./ImportPanel";
import { PercentileBar, traitName, useTraits } from "./ui";

interface Pickable { id: string; label: string; tag: string | null; class: string; status: string; sex: string; association: string; as_of: string | null }

export function compareColumns(animals: EpdAnimal[], association: string) {
  const cols = animals.map((a) => ({ a, s: a.snapshots.find((x) => associationKey(x.association) === associationKey(association)) ?? null }));
  const runs = runsOf(cols.filter((c) => c.s).map((c) => c.s!));
  const main = runs.sort((x, y) => y.count - x.count || (y.as_of ?? "").localeCompare(x.as_of ?? ""))[0];
  const keys = epdOrder([...new Set(cols.flatMap((c) => (c.s ? [...Object.keys(c.s.traits), ...Object.keys(c.s.indexes)] : [])))]);
  return { cols, runs, main, keys };
}

function best(keys: string[], cols: Array<{ s: EvaluationSnapshot | null }>, main?: { as_of: string | null; release?: string }) {
  const out = new Map<string, number>();
  for (const k of keys) {
    const dir = DEFAULT_TRAITS.find((d) => d.key === k)?.direction;
    if (dir !== "higher" && dir !== "lower") continue;
    const values = cols.filter((c) => c.s && (!main || (c.s.as_of === main.as_of && c.s.release === main.release))).map((c) => c.s!.traits[k]?.value ?? c.s!.indexes[k]?.value).filter((v): v is number => v !== undefined);
    if (values.length < 2) continue;
    out.set(k, dir === "higher" ? Math.max(...values) : Math.min(...values));
  }
  return out;
}

export function EpdTable({ animals, association }: { animals: EpdAnimal[]; association: string }) {
  const traits = useTraits();
  const { cols, runs, main, keys } = compareColumns(animals, association);
  const top = best(keys, cols, main);
  const sameRun = (s: EvaluationSnapshot | null) => !!s && !!main && s.as_of === main.as_of && s.release === main.release;
  if (!keys.length) return <Empty icon="report" title={t("No {{association}} EPDs for these animals", { association: associationShort(association) })} />;
  return (
    <div className="stack">
      {runs.length > 1 && (
        <div className="error-note" role="note" style={{ color: "var(--amber-ink)", background: "color-mix(in srgb, var(--amber) 12%, var(--paper-2))" }}>
          <Icon name="warn" size={18} />
          <span>{t("These come from different evaluation runs: {{runs}}. Shaded columns are from another run, so compare them with care.", { runs: runs.map((r) => r.release ?? (r.as_of ? formatDate(r.as_of) : t("Unknown date"))).join(", ") })}</span>
        </div>
      )}
      <div className="genetics-table-wrap">
        <table className="genetics-table">
          <thead>
            <tr>
              <th scope="col">{t("Trait")}</th>
              {cols.map(({ a, s }) => (
                <th key={a.id} scope="col" className={cx(s && !sameRun(s) && "is-other-run")}>
                  <Link to={`/cattle/${a.id}`}>{a.label}</Link>
                  <div>{s ? (s.release ?? (s.as_of ? formatDate(s.as_of) : t("Unknown date"))) : t("None")}</div>
                  {s?.genomic && <div>{t("Genomic-enhanced")}</div>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k}>
                <th scope="row"><strong>{k}</strong><div className="muted" style={{ fontWeight: 400, fontSize: 12.5 }}>{traitName(k, traits.data?.traits)}</div></th>
                {cols.map(({ a, s }) => {
                  const v = s ? s.traits[k] ?? s.indexes[k] : undefined;
                  return (
                    <td key={a.id} className={cx(s && !sameRun(s) && "is-other-run")}>
                      {v ? (
                        <div className="genetics-cell">
                          <span className={cx("genetics-epd-value", sameRun(s) && top.get(k) === v.value && "is-best")}>{formatEpd(v.value)}{v.acc !== undefined && <span className="genetics-epd-acc"> · {v.acc.toFixed(2)}</span>}</span>
                          {v.pct !== undefined && <PercentileBar pct={v.pct} />}
                        </div>
                      ) : <span className="muted">—</span>}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted">{t("Bold is the best value in the same run for traits with a clear direction. Accuracy follows the value. A dash means the run didn't report it.")}</p>
    </div>
  );
}

export function EpdPage() {
  useLanguage();
  const can = useCan();
  const [params, setParams] = useSearchParams();
  const ids = (params.get("ids") ?? "").split(",").filter(Boolean);
  const setIds = (next: string[]) => { const p = new URLSearchParams(params); if (next.length) p.set("ids", next.join(",")); else p.delete("ids"); setParams(p, { replace: true }); };
  const pickable = useApi<{ animals: Pickable[] }>("/m/genetics/epds/animals");
  const data = useApi<{ animals: EpdAnimal[] }>(ids.length ? `/m/genetics/epds?ids=${ids.join(",")}` : null);
  const associations = useMemo(() => {
    const counts = new Map<string, { name: string; n: number }>();
    for (const a of data.data?.animals ?? []) for (const s of a.snapshots) {
      const k = associationKey(s.association);
      const cur = counts.get(k) ?? { name: s.association, n: 0 };
      cur.n++;
      counts.set(k, cur);
    }
    return [...counts.values()].sort((x, y) => y.n - x.n);
  }, [data.data]);
  const [assoc, setAssoc] = useState<string | null>(null);
  const association = associations.find((x) => associationKey(x.name) === associationKey(assoc ?? ""))?.name ?? associations[0]?.name ?? null;
  const groups = useMemo(() => {
    const list = pickable.data?.animals ?? [];
    return [
      { id: "bulls", label: t("Herd bulls"), items: list.filter((a) => a.sex === "male" && a.status !== "reference" && a.class === "bull") },
      { id: "ref", label: t("Reference sires"), items: list.filter((a) => a.status === "reference") },
      { id: "other", label: t("Others with EPDs"), items: list.filter((a) => !(a.sex === "male" && a.status !== "reference" && a.class === "bull") && a.status !== "reference") },
    ].filter((g) => g.items.length);
  }, [pickable.data]);
  return (
    <GeneticsShell title={t("EPDs")} subtitle={t("Compare animals from the same association and run.")}>
      <Section title={t("Animals")}>
        <Field label={t("Add animals")}><RefPicker target="animal" multiple value={ids} onChange={(v) => setIds((v as string[]) ?? [])} filter={{ status: "all" }} /></Field>
        {groups.map((g) => (
          <div key={g.id} className="stack" style={{ gap: 6 }}>
            <span className="eyebrow">{g.label}</span>
            <div className="choice-chips">
              {g.items.map((a) => <button key={a.id} type="button" className={cx("choice", ids.includes(a.id) && "choice-on")} aria-pressed={ids.includes(a.id)} onClick={() => setIds(ids.includes(a.id) ? ids.filter((x) => x !== a.id) : [...ids, a.id])}>{a.label} <span className="muted">· {associationShort(a.association)}</span></button>)}
              {g.items.length > 1 && <button type="button" className="choice" onClick={() => setIds([...new Set([...ids, ...g.items.map((a) => a.id)])])}>{t("All")}</button>}
            </div>
          </div>
        ))}
      </Section>
      {!ids.length ? <Empty icon="report" title={t("Pick two or more animals to compare")} body={t("Tap the bulls above, or search for any animal.")} /> : data.isLoading ? <Loading /> : !data.data ? <ErrorNote error={data.error} /> : (
        <Section title={t("Side by side")}>
          {associations.length > 1 && <Segmented label={t("Association")} value={association ?? ""} onChange={setAssoc} options={associations.map((x) => ({ value: x.name, label: `${associationShort(x.name)} (${x.n})` }))} />}
          {associations.length > 1 && <p className="muted">{t("Each association has its own base. Numbers from different associations can't be compared directly.")}</p>}
          {association ? <EpdTable animals={data.data.animals} association={association} /> : <Empty icon="report" title={t("None of these animals have EPDs yet")} />}
          {association && (() => {
            const missing = data.data.animals.filter((a) => !a.snapshots.some((x) => associationKey(x.association) === associationKey(association))).length;
            if (!missing) return null;
            const values = { count: missing, association: associationShort(association) };
            return <p className="muted">{missing === 1 ? t("{{count}} animal has no {{association}} EPDs", values) : t("{{count}} animals have no {{association}} EPDs", values)}</p>;
          })()}
        </Section>
      )}
      {can("record") && (
        <Card>
          <Disclosure label={t("Bring in an EPD file")}>
            <ImportPanel kind="epd" onDone={() => void data.refetch()} />
          </Disclosure>
        </Card>
      )}
      <Chip tone="neutral" icon="info">{t("Typed-in and imported runs stay separate. Older runs are kept on each animal.")}</Chip>
    </GeneticsShell>
  );
}
